// Story 7.5 (ADO-909) — favourites state in a Zustand store (AC1/AC2).
//
// project-context React rules (ADR-004): Zustand for all client-side global
// state. The SERVER is the source of truth (supersedes the prototype's
// localStorage-only favourites-context, ux-design-specification-addendum.md:227):
// every mutation returns the full post-state list, which replaces the store.
//
// Story 17.1 (ADO-3722, ADR-024): the server answers the named lists of the
// caller's customer in the selected tile (the depot of the entry). The store keeps
// them as `lists` and derives two projections:
//   • `favouriteSkus` — a Set for the per-SKU heart toggle on every ProductCard
//     (cards subscribe with `s.favouriteSkus.has(sku)`, the same shape by which
//     they read `useAvailabilityStore`). A product is a favourite when it is on
//     ANY list of the tile; the heart adds to the default list and removes from
//     every list of the tile, as the server does.
//   • `favourites`: each favourited product once, in list order, for the counts
//     and the per-SKU reads (availability, prices) of the /favourites screen.
//
// Add/remove are OPTIMISTIC so the heart flips immediately (AC1 "favourited
// immediately"); on the server response the store reconciles to truth, and on a
// failure it rolls back the optimistic change (the heart never lies about a
// write that did not land).
//
// Enabler E7-C (ADO-2274), D-1: every call names the selected ordering entry when
// there is one, so each favourite is summarised from its row in that entry's range.
// The boot hydration runs before an entry exists and sends none (no tile, so no
// lists); an entry change re-hydrates the new tile's lists, and clearing the entry
// clears them (`clearLists`), both through `startCatalogueEntryResets`.
//
// LATEST OPERATION WINS. Reads and writes run one at a time, in the order they were
// asked for, so each answer is the server's list after every earlier operation. An
// answer is applied only when its operation is still the latest one asked for in the
// same session: a later read or write will bring a newer list, so an earlier answer
// (summarised under a previous entry, or from before a write) never overwrites it. A
// sign-out (`reset`) ends the session: operations still queued do not run, and an answer
// that lands afterwards is dropped. A write's optimistic heart and its rollback are per
// SKU and apply in the session whatever comes after, so the heart never lies.
//
// A WRITE BELONGS TO THE ENTRY IT WAS CLICKED IN (Story 17.1 review, ADO-3722). The heart
// captures the active entry key and the list generation at the click, and the request names that
// entry even when it runs after a switch. The generation is bumped on EVERY entry change, signed in
// or not: by `entryChanged()` for a new entry and by `clearLists()` for a cleared one, both called
// by `startCatalogueEntryResets`. So a switch away and back (A, B, A), or a clear and the same entry
// chosen again, still makes the write stale although the key matches again. The key is compared
// too, so a write whose entry is no longer active is stale even when no change was signalled. A
// stale write's answer, rollback and error are dropped. This holds whether or not a later read is
// queued: the ticket alone drops an answer only when a later operation exists.
//
// AN ENTRY CHANGE CLEARS THE TILE AT ONCE. `entryChanged()` and `clearLists()` empty the lists,
// the hearts, the pending flags and the error synchronously, before any read is sent, so the new
// depot's cards never show the previous depot's state while its read is out. A stale write then
// leaves the pending flags alone: the flag for its SKU, if any, belongs to a write clicked in the
// new entry. Only a write made stale by an unsignalled key change (no scope end, so nothing
// cleared the flags) releases its own flag.

import { create } from 'zustand';
import {
  addFavourite,
  listFavourites,
  removeFavourite,
  type FavouriteList,
  type FavouriteProduct,
} from '../api/favourites.client';
import { resolveCatalogueEntryKey } from '../ordering/catalogueEntry';

/** Bumped by `reset()` (sign-out): operations of an earlier session neither run nor land. */
let session = 0;
/** The ticket of the latest operation asked for; an answer lands only on that ticket. */
let latestTicket = 0;
/** The tail of the one-at-a-time queue. */
let queue: Promise<void> = Promise.resolve();
/**
 * Whether an answer of this session was dropped for a later operation since one last landed. If
 * that later operation then fails, nothing has brought the list, so it is read again.
 */
let answerDropped = false;
/**
 * Bumped by `entryChanged()` and `clearLists()`, on every entry change: a write clicked before it no
 * longer belongs to the lists on screen.
 */
let listGeneration = 0;

/** The entry and lists a heart was clicked against. */
interface WriteScope {
  readonly entryKey: string | null;
  readonly generation: number;
}

function captureWriteScope(): WriteScope {
  return { entryKey: resolveCatalogueEntryKey(), generation: listGeneration };
}

/**
 * The previous tile's scope ends (an entry change or a cleared entry): a newer ticket than every
 * operation asked for so far, so none of their answers is the latest, and a new generation, so a
 * write clicked before it neither lands nor rolls back nor touches the pending flags (the caller
 * clears them with the lists).
 */
function endTileScope(): void {
  latestTicket += 1;
  listGeneration += 1;
  answerDropped = false;
}

/** No write in flight and no error: the state of the write UI when a tile's scope begins. */
function noWriteUi() {
  return { error: false, pending: new Set<string>(), mutationError: null };
}

/**
 * A stale write's pending flag is its own to release only while no scope has ended since its click:
 * a scope end cleared every flag, and a flag for the same SKU now belongs to a write in the new
 * entry.
 */
function ownsItsPendingFlag(scope: WriteScope): boolean {
  return scope.generation === listGeneration;
}

/** The lists on screen are still those of the entry the write was clicked in. */
function isCurrentScope(scope: WriteScope): boolean {
  return scope.generation === listGeneration && scope.entryKey === resolveCatalogueEntryKey();
}

interface Operation {
  readonly session: number;
  readonly ticket: number;
}

/** In the operation's session, and nothing newer asked for since: its answer is the truth. */
function isLatest(operation: Operation): boolean {
  return operation.session === session && operation.ticket === latestTicket;
}

function inSession(operation: Operation): boolean {
  return operation.session === session;
}

/**
 * Queue `run` behind every earlier operation. It is skipped when the session has ended by its
 * turn. `run` handles its own failures, so the queue never stalls.
 */
function enqueue(run: (operation: Operation) => Promise<void>): Promise<void> {
  const operation: Operation = { session, ticket: ++latestTicket };
  const next = queue.then(() => (inSession(operation) ? run(operation) : undefined));
  queue = next.catch(() => undefined);
  return next;
}

interface FavouritesStoreState {
  /** The tile's lists, the default list first: drives the grouped /favourites screen. */
  lists: readonly FavouriteList[];
  /** Each favourited product once, in list order (derived from `lists`). */
  favourites: readonly FavouriteProduct[];
  /** Favourited SKU codes — drives the per-card heart toggle. */
  favouriteSkus: ReadonlySet<string>;
  /** True once the initial list has loaded (so hearts reflect real state). */
  hydrated: boolean;
  /** True when the last `hydrate` failed — lets the screen offer a retry rather
   *  than pinning the user on an infinite spinner (Story 7.5 review). */
  error: boolean;
  /** SKUs with an add/remove in flight — a per-SKU mutex so a double-click
   *  (add↔remove) cannot race two full-post-state reconciles to a wrong final
   *  state (Story 7.5 review). */
  pending: ReadonlySet<string>;
  /** The last add/remove that FAILED, keyed by SKU + the server's user-facing
   *  message (Story 7.5 review). Without this the optimistic heart just rolls
   *  back silently — at the per-user cap it flips on and straight back off with
   *  no explanation, on every retry. The card reads `mutationError.sku` to show
   *  the reason (e.g. the 500-favourite cap 400) beside the bouncing heart.
   *  Cleared when a new mutation for that SKU starts or succeeds. */
  mutationError: { readonly sku: string; readonly message: string } | null;
  /** Load the caller's favourites at boot / on entering a favourites-aware screen. */
  hydrate: () => Promise<void>;
  /** Optimistically favourite a SKU, then reconcile with the server (AC1/AC3). */
  add: (skuCode: string) => Promise<void>;
  /** Optimistically unfavourite a SKU, then reconcile with the server (AC2). */
  remove: (skuCode: string) => Promise<void>;
  /**
   * The entry was cleared (Story 17.1, ADO-3722): no tile is in view, so there is no list, as the
   * server answers a read without an entry. The previous tile's lists and hearts go at once, and
   * the answer of an operation still out for that tile no longer lands. The pending flags and the
   * error go too: writes in flight answer stale, so neither their answer nor their rollback lands.
   */
  clearLists: () => void;
  /**
   * The selected entry changed to another one (Story 17.1 review, ADO-3722): a favourite write
   * clicked in the previous entry is stale from here, its answer, rollback and error dropped. The
   * previous tile's lists, hearts, pending flags and error are cleared synchronously, so no card
   * shows the previous depot's state meanwhile. Called on every change, signed in or not.
   * `readFollows`: whether a read of the new entry follows, now (signed in) or from the sign-in
   * hydration (auth still initializing). When one does, `hydrated` goes false until it lands; when
   * none does (auth settled signed out, or failed), `hydrated` is left as it was, since no read
   * would set it back.
   */
  entryChanged: (options: { readonly readFollows: boolean }) => void;
  /** Test/session hygiene — drop all state. */
  reset: () => void;
}

/** Each product once, first appearance kept, in list order. */
function distinctProducts(lists: readonly FavouriteList[]): readonly FavouriteProduct[] {
  const seen = new Set<string>();
  const products: FavouriteProduct[] = [];
  for (const list of lists) {
    for (const product of list.favourites) {
      if (seen.has(product.sku)) continue;
      seen.add(product.sku);
      products.push(product);
    }
  }
  return products;
}

/** The server's lists and every projection of them. */
function fromLists(lists: readonly FavouriteList[]) {
  const favourites = distinctProducts(lists);
  return { lists, favourites, favouriteSkus: new Set(favourites.map((f) => f.sku)) };
}

/** Where a removed product sat, list by list, so a failed removal puts it back. */
interface RemovedPlacement {
  readonly listId: string;
  readonly index: number;
  readonly product: FavouriteProduct;
}

function withoutProduct(lists: readonly FavouriteList[], sku: string): readonly FavouriteList[] {
  return lists.map((list) =>
    list.favourites.some((f) => f.sku === sku)
      ? { ...list, favourites: list.favourites.filter((f) => f.sku !== sku) }
      : list,
  );
}

function placementsOf(lists: readonly FavouriteList[], sku: string): readonly RemovedPlacement[] {
  return lists.flatMap((list) => {
    const index = list.favourites.findIndex((f) => f.sku === sku);
    const product = list.favourites[index];
    return product === undefined ? [] : [{ listId: list.id, index, product }];
  });
}

/** Put the product back on each list it was taken from that does not hold it now. */
function withPlacements(
  lists: readonly FavouriteList[],
  placements: readonly RemovedPlacement[],
): readonly FavouriteList[] {
  return lists.map((list) => {
    const placement = placements.find((p) => p.listId === list.id);
    if (placement === undefined || list.favourites.some((f) => f.sku === placement.product.sku)) {
      return list;
    }
    const favourites = [...list.favourites];
    favourites.splice(Math.min(placement.index, favourites.length), 0, placement.product);
    return { ...list, favourites };
  });
}

function withSku(set: ReadonlySet<string>, sku: string): ReadonlySet<string> {
  const next = new Set(set);
  next.add(sku);
  return next;
}

function withoutSku(set: ReadonlySet<string>, sku: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(sku);
  return next;
}

/** The client throws an Error whose message is the server's user-facing text
 *  (favourites.client surfaces the Nest 400 body); fall back to a generic line
 *  if some non-Error was thrown. */
function messageOf(err: unknown): string {
  return err instanceof Error && err.message.length > 0
    ? err.message
    : 'Something went wrong. Please try again.';
}

export const useFavouritesStore = create<FavouritesStoreState>((set, get) => {
  /** A write's whole-lists answer: applied when its operation is the latest, else dropped. */
  const landOrDrop = (operation: Operation, lists: readonly FavouriteList[]): void => {
    if (!isLatest(operation)) {
      answerDropped = true;
      return;
    }
    answerDropped = false;
    set({ ...fromLists(lists), hydrated: true, error: false });
  };

  /** The latest operation failed after an earlier answer was dropped: read the list, in turn. */
  const readAgainIfNothingLanded = (operation: Operation): void => {
    if (isLatest(operation) && answerDropped) void get().hydrate();
  };

  return {
    lists: [],
    favourites: [],
    favouriteSkus: new Set<string>(),
    hydrated: false,
    error: false,
    pending: new Set<string>(),
    mutationError: null,

    hydrate() {
      set({ error: false });
      return enqueue(async (operation) => {
        try {
          // The entry as of this read's turn, not of the call: an entry change since queues
          // its own read, which is then the latest.
          const lists = await listFavourites(resolveCatalogueEntryKey());
          if (!isLatest(operation)) {
            if (inSession(operation)) answerDropped = true;
            return;
          }
          answerDropped = false;
          set({ ...fromLists(lists), hydrated: true, error: false });
        } catch {
          if (!isLatest(operation)) return;
          // A hydration failure must not blank hearts or crash a listing — leave the
          // current state (empty on first load) but FLAG the error so the screen can
          // surface a retry affordance instead of an infinite "Loading…" spinner.
          set({ error: true });
        }
      });
    },

    add(skuCode) {
      const { favouriteSkus, pending, mutationError } = get();
      // Already favourited (AC3 no-op) OR a mutation for this SKU is already in
      // flight — ignore the second click so two reconciles can't race.
      if (favouriteSkus.has(skuCode) || pending.has(skuCode)) return Promise.resolve();
      // Optimistic: fill the heart at once. We don't have the product detail here
      // (favouriting from a browse card), so only the SKU set changes until the
      // server returns the composed list. Clear any stale error for this SKU as we
      // retry so the message doesn't linger past a fresh attempt.
      set({
        favouriteSkus: withSku(favouriteSkus, skuCode),
        pending: withSku(pending, skuCode),
        mutationError: mutationError?.sku === skuCode ? null : mutationError,
      });
      const scope = captureWriteScope();
      return enqueue(async (operation) => {
        try {
          const lists = await addFavourite(skuCode, scope.entryKey);
          if (!inSession(operation)) return;
          if (!isCurrentScope(scope)) {
            if (ownsItsPendingFlag(scope)) set({ pending: withoutSku(get().pending, skuCode) });
            return;
          }
          set({ pending: withoutSku(get().pending, skuCode) });
          // The whole lists after this write; a later operation's lists supersede them.
          landOrDrop(operation, lists);
        } catch (err) {
          if (!inSession(operation)) return;
          if (!isCurrentScope(scope)) {
            if (ownsItsPendingFlag(scope)) set({ pending: withoutSku(get().pending, skuCode) });
            return;
          }
          // Roll back the optimistic heart against CURRENT state (not a stale
          // snapshot) so a concurrent mutation on another SKU is preserved, and
          // EXPOSE why (e.g. the per-user cap 400) so the card can tell the user
          // rather than the heart bouncing on/off with no explanation.
          set({
            favouriteSkus: withoutSku(get().favouriteSkus, skuCode),
            pending: withoutSku(get().pending, skuCode),
            mutationError: { sku: skuCode, message: messageOf(err) },
          });
          readAgainIfNothingLanded(operation);
        }
      });
    },

    remove(skuCode) {
      const { favouriteSkus, lists, pending, mutationError } = get();
      if (!favouriteSkus.has(skuCode) || pending.has(skuCode)) return Promise.resolve(); // no-op (AC2) or in-flight
      // The removal takes the product off every list of the tile (Story 17.1, ADO-3722).
      const placements = placementsOf(lists, skuCode);
      // Optimistic: unfill the heart AND drop it from every visible list. Clear any
      // stale error for this SKU as we retry.
      const remaining = withoutProduct(lists, skuCode);
      set({
        lists: remaining,
        favourites: distinctProducts(remaining),
        favouriteSkus: withoutSku(favouriteSkus, skuCode),
        pending: withSku(pending, skuCode),
        mutationError: mutationError?.sku === skuCode ? null : mutationError,
      });
      const scope = captureWriteScope();
      return enqueue(async (operation) => {
        try {
          const answer = await removeFavourite(skuCode, scope.entryKey);
          if (!inSession(operation)) return;
          if (!isCurrentScope(scope)) {
            if (ownsItsPendingFlag(scope)) set({ pending: withoutSku(get().pending, skuCode) });
            return;
          }
          set({ pending: withoutSku(get().pending, skuCode) });
          landOrDrop(operation, answer);
        } catch (err) {
          if (!inSession(operation)) return;
          if (!isCurrentScope(scope)) {
            // The placements belong to lists no longer on screen: putting them back would
            // repopulate a cleared store, or another entry's lists.
            if (ownsItsPendingFlag(scope)) set({ pending: withoutSku(get().pending, skuCode) });
            return;
          }
          // Roll back against CURRENT state — put the product back on each list it
          // was taken from, so a concurrent mutation on another SKU is not clobbered
          // by a stale full-lists snapshot — and EXPOSE why the removal did not land.
          const current = get();
          const restored = withPlacements(current.lists, placements);
          set({
            lists: restored,
            favourites: distinctProducts(restored),
            favouriteSkus: withSku(current.favouriteSkus, skuCode),
            pending: withoutSku(current.pending, skuCode),
            mutationError: { sku: skuCode, message: messageOf(err) },
          });
          readAgainIfNothingLanded(operation);
        }
      });
    },

    entryChanged({ readFollows }) {
      // Synchronously, before the new entry's read is even sent: the new depot's cards must not
      // show a heart, a pending flag or an error of the previous depot meanwhile.
      endTileScope();
      set({
        ...fromLists([]),
        hydrated: readFollows ? false : get().hydrated,
        ...noWriteUi(),
      });
    },

    clearLists() {
      endTileScope();
      set({ ...fromLists([]), hydrated: true, ...noWriteUi() });
    },

    reset() {
      // Ends the session: queued operations do not run, and answers still in flight are dropped.
      // The next session starts its own queue, so it never waits on a request of this one.
      session += 1;
      queue = Promise.resolve();
      answerDropped = false;
      set({
        lists: [],
        favourites: [],
        favouriteSkus: new Set<string>(),
        hydrated: false,
        error: false,
        pending: new Set<string>(),
        mutationError: null,
      });
    },
  };
});
